import React from 'react';
import {
	calculateDerivativeSubmittedPrice,
	DerivativePriceRule,
	shouldSynchronizeDerivativePrices,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/DerivativePriceSynchronization.utilities';

type SynchronizableRoom = {
	isAnchor: boolean,
	derivativeRule?: DerivativePriceRule,
};

type SubmittedRoomPrice = {
	submittedPrice: number | undefined,
};

const DerivativePriceSynchronizer: React.FunctionComponent<{
	form: any,
	rateIndex: number,
	rooms: SynchronizableRoom[],
	roomPrices: SubmittedRoomPrice[],
	suppressionEpoch: number,
}> = ({ form, rateIndex, rooms, roomPrices, suppressionEpoch }) => {
	const previousRoomPrices = React.useRef(roomPrices.map(room => room.submittedPrice));
	const previousSuppressionEpoch = React.useRef(suppressionEpoch);

	React.useEffect(() => {
		const currentRoomPrices = roomPrices.map(room => room.submittedPrice);
		const previousPrices = previousRoomPrices.current;
		const suppressionEpochChanged = previousSuppressionEpoch.current !== suppressionEpoch;
		previousRoomPrices.current = currentRoomPrices;
		previousSuppressionEpoch.current = suppressionEpoch;

		const anchorRoomIndex = rooms.findIndex(room => room.isAnchor);
		if (anchorRoomIndex < 0) return;

		// Opening and batch operations such as reset/bulk adjustment must preserve
		// all supplied values. Cascade only when the anchor alone was edited.
		if (!shouldSynchronizeDerivativePrices(
			previousPrices,
			currentRoomPrices,
			anchorRoomIndex,
			suppressionEpochChanged,
		)) return;

		const anchorSubmittedPrice = currentRoomPrices[anchorRoomIndex];

		rooms.forEach((room, roomIndex) => {
			if (room.isAnchor) return;

			form.setFieldValue(
				`rates[${rateIndex}].roomPrices[${roomIndex}].submittedPrice`,
				anchorSubmittedPrice === undefined
					? undefined
					: calculateDerivativeSubmittedPrice(anchorSubmittedPrice, room.derivativeRule),
			);
		});
	}, [form, rateIndex, roomPrices, rooms, suppressionEpoch]);

	return null;
};

export default DerivativePriceSynchronizer;
